import { Typography, IconButton } from "@mui/material";
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import React, { useEffect, useState } from "react";
import { t } from "../../features/i18n";

type Props = {
  src: string;
  scale: number;
  percent: number;
  isLoad: boolean;
  totalImages: number;  // Add this prop
  onImageChange: (index: number) => void;  // Add this prop
  currentIndex: number;  // Add this prop
  onZoom?: (scale: number) => void;  // Add onZoom back to Props
};

const ImageView: React.FC<Props> = ({ 
  src, 
  scale, 
  percent, 
  isLoad,
  totalImages,
  onImageChange,
  currentIndex,
  onZoom,
}) => {
  const [imageSize, setImageSize] = useState({ width: "auto", height: "auto" });
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [lastX, setLastX] = useState(0);
  const [isHovering, setIsHovering] = useState(false);
  const containerRef = React.useRef<HTMLDivElement>(null);

  useEffect(() => {
    try {
      const img = new Image();
      img.src = src;
      img.onload = () => {
        const { naturalWidth, naturalHeight } = img;
        if (naturalWidth > naturalHeight) {
          setImageSize({ width: "100%", height: "auto" });
        } else {
          setImageSize({ width: "auto", height: "100%" });
        }
      };
    } catch (error) {
      console.log(error)
    }
  }, [src]);

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      containerRef.current?.requestFullscreen();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  };

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  const handleWheel = (event: React.WheelEvent<HTMLDivElement>) => {
    if (isHovering) {
      event.preventDefault();
      event.stopPropagation();  // Add this to prevent event bubbling
      
      const delta = -Math.sign(event.deltaY) * 0.1;
      const newScale = Math.max(0.5, Math.min(3, scale + delta));
      if (onZoom) {
        onZoom(newScale);
      }
      return false;  // Prevent default browser behavior
    }
  };

  useEffect(() => {
    const container = containerRef.current;
    if (container) {
      const preventScroll = (e: WheelEvent) => {
        if (isHovering) {
          e.preventDefault();
        }
      };

      container.addEventListener('wheel', preventScroll, { passive: false });
      return () => {
        container.removeEventListener('wheel', preventScroll);
      };
    }
  }, [isHovering]);

  const handleMouseDown = (e: React.MouseEvent) => {
    setIsDragging(true);
    setStartX(e.clientX);
    setLastX(e.clientX);
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging) return;

    const deltaX = e.clientX - lastX;
    setLastX(e.clientX);

    const sensitivity = 2;
    const movement = Math.round(deltaX / sensitivity);
    
    if (movement !== 0) {
      let newIndex = currentIndex - movement;
      newIndex = ((newIndex % totalImages) + totalImages) % totalImages;
      onImageChange(newIndex);
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    setIsDragging(true);
    setStartX(e.touches[0].clientX);
    setLastX(e.touches[0].clientX);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!isDragging) return;

    const deltaX = e.touches[0].clientX - lastX;
    setLastX(e.touches[0].clientX);

    const sensitivity = 2;
    const movement = Math.round(deltaX / sensitivity);
    
    if (movement !== 0) {
      let newIndex = currentIndex - movement;
      newIndex = ((newIndex % totalImages) + totalImages) % totalImages;
      onImageChange(newIndex);
    }
  };

  const handleTouchEnd = () => {
    setIsDragging(false);
  };

  const handleMouseEnter = () => {
    setIsHovering(true);
  };

  const handleMouseLeave = () => {
    setIsHovering(false);
  };

  const containerStyle: React.CSSProperties = {
    width: isFullscreen ? "100vw" : "800px",
    height: isFullscreen ? "100vh" : "600px",
    border: "5px solid white",
    background: "white",
    borderRadius: isFullscreen ? "0" : "10px",
    overflow: "hidden",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    position: "relative",
    cursor: isDragging ? 'grabbing' : 'grab',
    userSelect: 'none',
  };

  const fullscreenButtonStyle: React.CSSProperties = {
    position: "absolute",
    top: "10px",
    right: "10px",
    zIndex: 1000,
    background: "rgba(255, 255, 255, 0.7)",
    borderRadius: "50%",
  };

  const imageStyle: React.CSSProperties = {
    width: imageSize.width,
    height: imageSize.height,
    objectFit: "contain",
    transform: `scale(${scale})`,
  };

  return (
    <div 
      style={{ 
        overflow: isHovering ? 'hidden' : 'auto',
        height: isFullscreen ? '100vh' : '600px'
      }}
    >
      <div 
        style={containerStyle} 
        ref={containerRef}
        onWheel={(e) => {
          e.preventDefault();
          if (isHovering && onZoom) {
            const delta = -Math.sign(e.deltaY) * 0.1;
            const newScale = Math.max(0.5, Math.min(3, scale + delta));
            onZoom(newScale);
          }
        }}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={() => {
          handleMouseUp();
          handleMouseLeave();        }}        onMouseEnter={handleMouseEnter}        onTouchStart={handleTouchStart}        onTouchMove={handleTouchMove}        onTouchEnd={handleTouchEnd}      >        <IconButton           onClick={toggleFullscreen}          style={fullscreenButtonStyle}          size="large"        >
          {isFullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
        </IconButton>
        {isLoad ? (
          <img src={src} alt={`Obrázek + ${src}`} style={imageStyle} />
        ) : (
          <Typography>{t("loading_images")}{percent} %</Typography>
        )}
      </div>
    </div>
  );
};

export default ImageView;
